import { useMemo, useCallback } from "react";
import { BROKER_ACTIVACION } from "../../../constants/permisos";
import {
  calcularComisionEdificiosTotal,
  calcularImporteNetoIntermediario,
  calcularPorcentajeComisionIntermediario,
  calculateComission,
  obtenerNombreIntermediario,
} from "../../../utils";
import { getApxMonto } from "../../../utils/apx";
import { getImporteFacturable } from "../../../utils/getImporteFacturable";
import { resolverPorcentajeBroker } from "../../../utils/resolverPorcentajeBroker";

/**
 * Hook que calcula ingresos mensuales y comisión de edificios de contratos (sales_contratos).
 *
 * Explota el JSONB `meses` de cada contrato y agrupa por mes según `facturafecha`.
 * ER solo cuenta meses realmente facturados (con facturafecha), no proyectados.
 */
export function useContratosCalcs({
  contratosChile,
  contratosPeru,
  selectedYear,
  modoCombinado,
  dolarFallback,
  getDolarValue,
  getDolarValueChile,
  getDolarValuePeru,
  dolarMensualChile,
  dolarMensualPeru,
  pais,
  edificios,
  edificiosChile,
  edificiosPeru,
  snapMap,
  snapMapChile,
  snapMapPeru,
  pctMap,
  pctMapChile,
  pctMapPeru,
  clientes,
  clientesChile,
  clientesPeru,
  intermediarios,
  intermediariosChile,
  intermediariosPeru,
}) {
  // Helper: calcula ingresos mensuales explotando JSONB meses
  const calcIngresosContratos = useCallback((data, filterYear, getDolarFn) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha) return; // ER: solo facturado real
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year === filterYear && monthIndex >= 0 && monthIndex < 12) {
            // Ingreso facturable: descuento de acuerdo a nivel contrato (ej. OMNET 25%, ver 7.22).
            const importe = getImporteFacturable({
              importe: mesObj.importe,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
            });
            valores[monthIndex] += c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
          }
        });
      });
    return valores;
  }, []);

  // Helper: backward-compat para campos por tipo de pantalla
  const resolverCamposTipo = (mesObj, edificiosMap) => {
    let pAsc = mesObj.pantallas_ascensores || "";
    let pEsp = mesObj.pantallas_espera || "";
    let pCir = mesObj.pantallas_circulacion || "";
    let pCow = mesObj.pantallas_cowork || "";
    if (!pAsc && !pEsp && !pCir && !pCow && mesObj.edificios_ids) {
      const ids = mesObj.edificios_ids
        .split(",")
        .map((s) => Number(s.trim()))
        .filter(Boolean);
      const asc = [];
      const esp = [];
      const cir = [];
      const cow = [];
      ids.forEach((eid) => {
        const e = edificiosMap[eid];
        if (!e) return;
        if ((Number(e.pantallas) || 0) > 0) asc.push(eid);
        if ((Number(e.espera) || 0) > 0) esp.push(eid);
        if ((Number(e.totems) || 0) > 0) cir.push(eid);
        if ((Number(e.cowork) || 0) > 0) cow.push(eid);
      });
      pAsc = asc.join(",");
      pEsp = esp.join(",");
      pCir = cir.join(",");
      pCow = cow.join(",");
    }
    return { pAsc, pEsp, pCir, pCow };
  };

  // Helper: calcula comisión edificios mensual explotando JSONB meses
  const calcComisionEdificiosContratos = useCallback(
    (data, filterYear, getDolarFn, edificiosMap, clientesArr, intermediariosArr, paisCalc, snapMapArg, pctMapArg) => {
      const valores = Array(12).fill(0);
      if (!edificiosMap || Object.keys(edificiosMap).length === 0) return valores;

      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            if (!mesObj.edificios_ids) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = Number(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificiosMap);

            const oppObj = {
              id: `contrato-${c.id}-${mesObj.mes}`,
              edificiosparticipantes: mesObj.edificios_ids,
              pantallastotales: mesObj.pantallas_totales || 0,
              pantallas_ascensores: pAsc,
              pantallas_espera: pEsp,
              pantallas_circulacion: pCir,
              pantallas_cowork: pCow,
              facturafecha: mesObj.facturafecha,
              importe: importeLocal,
              client_id: c.client_id,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
              apx_id: c.apx_id,
              isBTL: false,
            };

            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, paisCalc, pctMapArg);
            // APX (Publicis): apx_id vive a nivel contrato → aplica a todos los meses. Baja la base de
            // edificios igual que en pauta (capa aislada 10.48). getApxMonto = importeLocal_mes × %.
            // PENDIENTE FASE 6: take por mes en contratos (sales_contratos sin take hoy) → takeLocal=0.
            const apxLocal = getApxMonto(oppObj, intermediariosArr, pctMapArg);
            valores[monthIndex] += calcularComisionEdificiosTotal(oppObj, edificiosMap, neto, snapMapArg, 0, apxLocal);
          });
        });
      return valores;
    },
    []
  );

  // Helper: calcula comisión agencia mensual explotando JSONB meses
  const calcComisionAgenciaContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr, intermediariosArr, paisCalc, pctMapArg) => {
      const valores = Array(12).fill(0);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const oppObj = {
              id: `contrato-${c.id}-${mesObj.mes}`,
              importe: importeLocal,
              client_id: c.client_id,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
              facturafecha: mesObj.facturafecha,
              isBTL: false,
            };
            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, paisCalc, pctMapArg);
            valores[monthIndex] += importeLocal - neto;
          });
        });
      return valores;
    },
    []
  );

  // Helper: comisión BROKER mensual de contratos (2ª capa, capa aislada/aditiva)
  // Espejo de calcComisionAgenciaContratos pero derivada de cliente.broker_id con gate
  // DOBLE (BROKER_ACTIVACION + fecha del registro) sobre mesObj.facturafecha directo.
  const calcComisionBrokerContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr, intermediariosArr, paisCalc, pctMapArg) => {
      const valores = Array(12).fill(0);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            // % de broker (post-gate) desde la fuente única (mini-unify A3b-2); 0 si no aplica.
            // A3: con pctMapArg lee el % congelado (id → keyDeFactura contrato).
            const porcentajeBroker = resolverPorcentajeBroker(
              { id: `contrato-${c.id}-${mesObj.mes}`, client_id: c.client_id, pais: paisCalc },
              clientesArr,
              intermediariosArr,
              fecha,
              pctMapArg
            );
            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            valores[monthIndex] += (importeLocal * porcentajeBroker) / 100;
          });
        });
      return valores;
    },
    []
  );

  // Helper: DESGLOSE por factura de comisión AGENCIA de contratos (alimenta el modal).
  // Espejo EXACTO de calcComisionAgenciaContratos (misma conversión importeLocal, mismo
  // filtro, misma fórmula comisión = importeLocal − neto) pero hace push por mesObj.
  const calcDesgloseAgenciaContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr2, intermArr2, paisCalc, pctMapArg) => {
      const comisiones = Array(12)
        .fill()
        .map(() => []);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            // `importeLocal` (lista) va al oppObj → el neto descuenta internamente (guarda 7.22). Para el
            // display y la comisión usamos el facturable (espeja la sección Agencia-pauta); así OMNET da
            // comisión 0 (facturable − neto) en vez de la comisión fantasma = descuento. Sin descuento
            // → facturable = importe → contratos reales intactos.
            const facturableLocal = getImporteFacturable({
              importe: importeLocal,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
            });
            const oppObj = {
              id: `contrato-${c.id}-${mesObj.mes}`,
              importe: importeLocal,
              client_id: c.client_id,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
              facturafecha: mesObj.facturafecha,
              isBTL: false,
            };
            const neto = calcularImporteNetoIntermediario(oppObj, clientesArr2, intermArr2, paisCalc, pctMapArg);
            comisiones[monthIndex].push({
              intermediario: obtenerNombreIntermediario(oppObj, clientesArr2, intermArr2, paisCalc),
              facturanumero: mesObj.facturanumero || "—",
              comision: facturableLocal - neto,
              porcentaje: calcularPorcentajeComisionIntermediario(
                oppObj,
                clientesArr2,
                intermArr2,
                paisCalc,
                pctMapArg
              ),
              importe: facturableLocal,
              importeOriginalMoneda: getImporteFacturable({ importe, descuento_acuerdo_pct: c.descuento_acuerdo_pct }),
              moneda_dolar: !!c.moneda_dolar,
              oppId: `contrato-${c.id}-${mesObj.mes ?? mesObj.facturafecha}`,
              cliente: c.cliente,
            });
          });
        });
      return comisiones;
    },
    []
  );

  // Helper: DESGLOSE por factura de comisión BROKER de contratos (alimenta el modal).
  // Espejo EXACTO de calcComisionBrokerContratos (misma conversión + gate doble) con push por mesObj.
  const calcDesgloseBrokerContratos = useCallback(
    (data, filterYear, getDolarFn, clientesArr2, intermArr2, paisCalc, pctMapArg) => {
      const comisiones = Array(12)
        .fill()
        .map(() => []);
      const activacion = new Date(BROKER_ACTIVACION);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const cliente = clientesArr2.find((cl) => cl.id === c.client_id && cl.pais === paisCalc);
            if (!cliente?.broker_id) return;
            const broker = intermArr2.find((i) => i.id === parseInt(cliente.broker_id, 10) && i.pais === paisCalc);
            if (!broker?.comision_fechainicio) return;
            if (fecha < activacion || fecha < new Date(broker.comision_fechainicio)) return;

            const importe = parseFloat(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            // Columna "Importe" del modal → facturable (7.22), como el desglose de pauta. Sin descuento
            // → = importeLocal. La comisión de broker sigue sobre importeLocal (fuera de este scope).
            const facturableLocal = getImporteFacturable({
              importe: importeLocal,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
            });
            // % de broker (post-gate) desde la fuente única (mini-unify A3b-2); el objeto broker sigue
            // resuelto arriba solo para el nombre a mostrar (display, nunca congelado).
            // A3: con pctMapArg lee el % congelado (id → keyDeFactura contrato).
            const porcentajeBroker = resolverPorcentajeBroker(
              { id: `contrato-${c.id}-${mesObj.mes}`, client_id: c.client_id, pais: paisCalc },
              clientesArr2,
              intermArr2,
              fecha,
              pctMapArg
            );
            comisiones[monthIndex].push({
              brokerNombre: broker.nombre,
              facturanumero: mesObj.facturanumero || "—",
              comisionBroker: (importeLocal * porcentajeBroker) / 100,
              porcentajeBroker,
              importe: facturableLocal,
              importeOriginalMoneda: getImporteFacturable({ importe, descuento_acuerdo_pct: c.descuento_acuerdo_pct }),
              moneda_dolar: !!c.moneda_dolar,
              oppId: `contrato-${c.id}-${mesObj.mes ?? mesObj.facturafecha}`,
              cliente: c.cliente,
            });
          });
        });
      return comisiones;
    },
    []
  );

  // Helper: comisión APX mensual de contratos (capa Publicis aditiva, molde take/FASE 6).
  // apx_id vive a nivel CONTRATO (sales_contratos.apx_id) → aplica a TODOS sus meses. El monto es
  // importeLocal_del_mes × (intermediarios[apx_id].comision/100); getApxMonto resuelve por ID (10.7).
  // Es el COSTO que entra a los 4 agregados del ER (línea "Costo intermediación"), además de bajar
  // la base de edificios/comercial. Espejo de calcComisionAgenciaContratos.
  const calcComisionApxContratos = useCallback((data, filterYear, getDolarFn, intermediariosArr2, pctMapArg) => {
    const valores = Array(12).fill(0);
    data
      .filter((c) => !c.eliminada && c.estatus === "100%" && c.apx_id != null)
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;
          const importe = parseFloat(mesObj.importe) || 0;
          const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
          valores[monthIndex] += getApxMonto(
            { id: `contrato-${c.id}-${mesObj.mes}`, apx_id: c.apx_id, importe: importeLocal },
            intermediariosArr2,
            pctMapArg
          );
        });
      });
    return valores;
  }, []);

  // Helper: DESGLOSE por factura de APX de contratos (alimenta el grupo "APX" del modal). Espejo de
  // calcDesgloseAgenciaContratos. porcentaje = intermediarios[apx_id].comision (ej. 9).
  const calcDesgloseApxContratos = useCallback((data, filterYear, getDolarFn, intermArr2, pctMapArg) => {
    const comisiones = Array(12)
      .fill()
      .map(() => []);
    data
      .filter((c) => !c.eliminada && c.estatus === "100%" && c.apx_id != null)
      .forEach((c) => {
        const apxInter = intermArr2.find((i) => i.id === parseInt(c.apx_id, 10));
        const porcentaje = apxInter?.comision || 0;
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;
          const importe = parseFloat(mesObj.importe) || 0;
          const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
          // Columna "Importe" del modal → facturable (7.22), como el desglose de pauta. Sin descuento → = importeLocal.
          const facturableLocal = getImporteFacturable({
            importe: importeLocal,
            descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          });
          comisiones[monthIndex].push({
            facturanumero: mesObj.facturanumero || "—",
            apxLocal: getApxMonto(
              { id: `contrato-${c.id}-${mesObj.mes}`, apx_id: c.apx_id, importe: importeLocal },
              intermArr2,
              pctMapArg
            ),
            porcentaje,
            importe: facturableLocal,
            importeOriginalMoneda: getImporteFacturable({ importe, descuento_acuerdo_pct: c.descuento_acuerdo_pct }),
            moneda_dolar: !!c.moneda_dolar,
            oppId: `contrato-${c.id}-${mesObj.mes ?? mesObj.facturafecha}`,
            cliente: c.cliente,
          });
        });
      });
    return comisiones;
  }, []);

  // Helper: calcula comisión comercial mensual explotando JSONB meses
  const calcComisionComercialContratos = useCallback(
    (data, filterYear, getDolarFn, clientesMap, intermediariosMap, edificiosMap, paisCalc, snapMapArg, pctMapArg) => {
      const valores = Array(12).fill(0);
      data
        .filter((c) => !c.eliminada && c.estatus === "100%")
        .forEach((c) => {
          (c.meses || []).forEach((mesObj) => {
            if (!mesObj.facturafecha) return;
            const fecha = new Date(mesObj.facturafecha);
            if (isNaN(fecha.getTime())) return;
            const monthIndex = fecha.getMonth();
            const year = fecha.getFullYear();
            if (year !== filterYear || monthIndex < 0 || monthIndex >= 12) return;

            const importe = Number(mesObj.importe) || 0;
            const importeLocal = c.moneda_dolar ? importe * getDolarFn(mesObj.facturafecha) : importe;
            const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificiosMap || {});
            const oppObj = {
              id: `contrato-${c.id}-${mesObj.mes}`,
              importe: importeLocal,
              client_id: c.client_id,
              descuento_acuerdo_pct: c.descuento_acuerdo_pct,
              apx_id: c.apx_id,
              pais: paisCalc,
              facturafecha: mesObj.facturafecha,
              ejecutivo: c.ejecutivo,
              mes_cierre: c.mes_cierre,
              moneda_dolar: false,
              isBTL: false,
              edificiosparticipantes: mesObj.edificios_ids || "",
              pantallastotales: mesObj.pantallas_totales || 0,
              pantallas_ascensores: pAsc,
              pantallas_espera: pEsp,
              pantallas_circulacion: pCir,
              pantallas_cowork: pCow,
            };
            // APX: con apx_id en el oppObj, calculateSalesCommission resuelve getApxMonto solo
            // (baja base edificios + neto comercial) sobre el importe del MES. take sigue 0 (pendiente).
            valores[monthIndex] += calculateComission.calculateSalesCommission(
              oppObj,
              clientesMap,
              intermediariosMap,
              edificiosMap,
              undefined,
              snapMapArg,
              0, // PENDIENTE FASE 6 etapa posterior: take por mes en contratos (sales_contratos: 0 filas hoy)
              pctMapArg
            );
          });
        });
      return valores;
    },
    []
  );

  // País actual
  const contratosData = pais === "Chile" ? contratosChile : contratosPeru;

  // Memoize arrays para evitar re-renders en dependencias de useMemo
  const clientesArr = useMemo(() => (clientes ? Object.values(clientes) : []), [clientes]);
  const intermediariosArr = useMemo(() => (intermediarios ? Object.values(intermediarios) : []), [intermediarios]);
  const clientesChileArr = useMemo(() => (clientesChile ? Object.values(clientesChile) : []), [clientesChile]);
  const intermediariosChileArr = useMemo(
    () => (intermediariosChile ? Object.values(intermediariosChile) : []),
    [intermediariosChile]
  );
  const clientesPeruArr = useMemo(() => (clientesPeru ? Object.values(clientesPeru) : []), [clientesPeru]);
  const intermediariosPeruArr = useMemo(
    () => (intermediariosPeru ? Object.values(intermediariosPeru) : []),
    [intermediariosPeru]
  );

  // ─── FACTURAS POR MES (para InvoiceModal) ───

  const facturasPorMesContratos = useMemo(() => {
    const facturas = Array(12)
      .fill()
      .map(() => []);
    contratosData
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha || !mesObj.facturanumero) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== selectedYear || monthIndex < 0 || monthIndex >= 12) return;
          // Facturación → facturable (descuento de acuerdo a nivel contrato, 7.22); espeja el ítem pauta
          // (usePublicidadCalcs). Sin descuento → = importe (no-OMNET intacto). El InvoiceModal muestra
          // importeOriginalMoneda en la rama USD, por eso también va facturable.
          const importe = getImporteFacturable({
            importe: mesObj.importe,
            descuento_acuerdo_pct: c.descuento_acuerdo_pct,
          });
          const importeLocal = c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;
          facturas[monthIndex].push({
            facturanumero: mesObj.facturanumero,
            cliente: c.cliente || "Sin cliente",
            importe: importeLocal,
            importeOriginalMoneda: importe,
            moneda_dolar: !!c.moneda_dolar,
          });
        });
      });
    return facturas;
  }, [contratosData, selectedYear, getDolarValue]);

  // ─── COMISIONES EDIFICIOS POR MES (para BuildingCommissionModal) ───

  const comisionesEdificiosPorMesContratos = useMemo(() => {
    const comisiones = Array(12)
      .fill()
      .map(() => []);
    if (!edificios || Object.keys(edificios).length === 0) return comisiones;
    contratosData
      .filter((c) => !c.eliminada && c.estatus === "100%")
      .forEach((c) => {
        (c.meses || []).forEach((mesObj) => {
          if (!mesObj.facturafecha || !mesObj.edificios_ids) return;
          const fecha = new Date(mesObj.facturafecha);
          if (isNaN(fecha.getTime())) return;
          const monthIndex = fecha.getMonth();
          const year = fecha.getFullYear();
          if (year !== selectedYear || monthIndex < 0 || monthIndex >= 12) return;

          const importe = Number(mesObj.importe) || 0;
          const importeLocal = c.moneda_dolar ? importe * getDolarValue(mesObj.facturafecha) : importe;
          const { pAsc, pEsp, pCir, pCow } = resolverCamposTipo(mesObj, edificios);
          const oppObj = {
            id: `contrato-${c.id}-${mesObj.mes}`,
            edificiosparticipantes: mesObj.edificios_ids,
            pantallastotales: mesObj.pantallas_totales || 0,
            pantallas_ascensores: pAsc,
            pantallas_espera: pEsp,
            pantallas_circulacion: pCir,
            pantallas_cowork: pCow,
            facturafecha: mesObj.facturafecha,
            importe: importeLocal,
            client_id: c.client_id,
            descuento_acuerdo_pct: c.descuento_acuerdo_pct,
            apx_id: c.apx_id,
            isBTL: false,
          };
          const neto = calcularImporteNetoIntermediario(oppObj, clientesArr, intermediariosArr, pais, pctMap);
          // APX baja la base (igual que la línea del ER). take sigue 0 (pendiente).
          const apxLocal = getApxMonto(oppObj, intermediariosArr, pctMap);
          const totalComision = calcularComisionEdificiosTotal(oppObj, edificios, neto, snapMap, 0, apxLocal);
          const edIds = mesObj.edificios_ids ? [...new Set(mesObj.edificios_ids.split(",").map(Number))] : [];
          comisiones[monthIndex].push({
            facturanumero: mesObj.facturanumero || "—",
            cliente: c.cliente || "Sin cliente",
            totalComision,
            soloFijos: false,
            sinEdificios: edIds.length === 0,
          });
        });
      });
    return comisiones;
  }, [contratosData, selectedYear, getDolarValue, edificios, snapMap, pctMap, clientesArr, intermediariosArr, pais]);

  // ─── INGRESOS ───

  const ingresosContratosMensual = useMemo(
    () => calcIngresosContratos(contratosData, selectedYear, getDolarValue),
    [calcIngresosContratos, contratosData, selectedYear, getDolarValue]
  );

  const ingresosContratosMensualAnoAnterior = useMemo(
    () => calcIngresosContratos(contratosData, selectedYear - 1, getDolarValue),
    [calcIngresosContratos, contratosData, selectedYear, getDolarValue]
  );

  const ingresosContratosMensualChile = useMemo(
    () => calcIngresosContratos(contratosChile, selectedYear, getDolarValueChile),
    [calcIngresosContratos, contratosChile, selectedYear, getDolarValueChile]
  );

  const ingresosContratosMensualPeru = useMemo(
    () => calcIngresosContratos(contratosPeru, selectedYear, getDolarValuePeru),
    [calcIngresosContratos, contratosPeru, selectedYear, getDolarValuePeru]
  );

  // Combinado en USD (para modo región)
  const contratosMensualesCombinados = useMemo(() => {
    if (!modoCombinado) return null;
    const resultado = Array(12).fill(0);
    for (let i = 0; i < 12; i++) {
      const valorChile = ingresosContratosMensualChile[i] || 0;
      const dolarChile = dolarMensualChile[i] || dolarFallback.Chile;
      const chileUSD = valorChile / dolarChile;

      const valorPeru = ingresosContratosMensualPeru[i] || 0;
      const dolarPeru = dolarMensualPeru[i] || dolarFallback["Perú"];
      const peruUSD = valorPeru / dolarPeru;

      resultado[i] = chileUSD + peruUSD;
    }
    return resultado;
  }, [
    modoCombinado,
    ingresosContratosMensualChile,
    ingresosContratosMensualPeru,
    dolarMensualChile,
    dolarMensualPeru,
    dolarFallback,
  ]);

  // ─── COMISIÓN EDIFICIOS de contratos ───

  const comisionEdificiosContratosMensual = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        edificios,
        clientesArr,
        intermediariosArr,
        pais,
        snapMap,
        pctMap
      ),
    [
      calcComisionEdificiosContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      edificios,
      snapMap,
      pctMap,
      clientesArr,
      intermediariosArr,
      pais,
    ]
  );

  const comisionEdificiosContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        edificios,
        clientesArr,
        intermediariosArr,
        pais,
        snapMap,
        pctMap
      ),
    [
      calcComisionEdificiosContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      edificios,
      snapMap,
      pctMap,
      clientesArr,
      intermediariosArr,
      pais,
    ]
  );

  const comisionEdificiosContratosMensualChile = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        edificiosChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile",
        snapMapChile,
        pctMapChile
      ),
    [
      calcComisionEdificiosContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      edificiosChile,
      snapMapChile,
      pctMapChile,
      clientesChileArr,
      intermediariosChileArr,
    ]
  );

  const comisionEdificiosContratosMensualPeru = useMemo(
    () =>
      calcComisionEdificiosContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        edificiosPeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú",
        snapMapPeru,
        pctMapPeru
      ),
    [
      calcComisionEdificiosContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      edificiosPeru,
      snapMapPeru,
      pctMapPeru,
      clientesPeruArr,
      intermediariosPeruArr,
    ]
  );

  // ─── COMISIÓN AGENCIA de contratos ───

  const comisionAgenciaContratosMensual = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais,
        pctMap
      ),
    [
      calcComisionAgenciaContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientesArr,
      intermediariosArr,
      pais,
      pctMap,
    ]
  );

  const comisionAgenciaContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais,
        pctMap
      ),
    [
      calcComisionAgenciaContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientesArr,
      intermediariosArr,
      pais,
      pctMap,
    ]
  );

  const comisionAgenciaContratosMensualChile = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile",
        pctMapChile
      ),
    [
      calcComisionAgenciaContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      clientesChileArr,
      intermediariosChileArr,
      pctMapChile,
    ]
  );

  const comisionAgenciaContratosMensualPeru = useMemo(
    () =>
      calcComisionAgenciaContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú",
        pctMapPeru
      ),
    [
      calcComisionAgenciaContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      clientesPeruArr,
      intermediariosPeruArr,
      pctMapPeru,
    ]
  );

  // ─── COMISIÓN BROKER de contratos (capa aislada/aditiva) ───
  const comisionBrokerContratosMensual = useMemo(
    () =>
      calcComisionBrokerContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais,
        pctMap
      ),
    [
      calcComisionBrokerContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientesArr,
      intermediariosArr,
      pais,
      pctMap,
    ]
  );

  const comisionBrokerContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionBrokerContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais,
        pctMap
      ),
    [
      calcComisionBrokerContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientesArr,
      intermediariosArr,
      pais,
      pctMap,
    ]
  );

  const comisionBrokerContratosMensualChile = useMemo(
    () =>
      calcComisionBrokerContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        clientesChileArr,
        intermediariosChileArr,
        "Chile",
        pctMapChile
      ),
    [
      calcComisionBrokerContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      clientesChileArr,
      intermediariosChileArr,
      pctMapChile,
    ]
  );

  const comisionBrokerContratosMensualPeru = useMemo(
    () =>
      calcComisionBrokerContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        clientesPeruArr,
        intermediariosPeruArr,
        "Perú",
        pctMapPeru
      ),
    [
      calcComisionBrokerContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      clientesPeruArr,
      intermediariosPeruArr,
      pctMapPeru,
    ]
  );

  // ─── COMISIÓN APX de contratos (capa Publicis aditiva, molde take) ───
  const comisionApxContratosMensual = useMemo(
    () => calcComisionApxContratos(contratosData, selectedYear, getDolarValue, intermediariosArr, pctMap),
    [calcComisionApxContratos, contratosData, selectedYear, getDolarValue, intermediariosArr, pctMap]
  );

  const comisionApxContratosMensualAnoAnterior = useMemo(
    () => calcComisionApxContratos(contratosData, selectedYear - 1, getDolarValue, intermediariosArr, pctMap),
    [calcComisionApxContratos, contratosData, selectedYear, getDolarValue, intermediariosArr, pctMap]
  );

  const comisionApxContratosMensualChile = useMemo(
    () =>
      calcComisionApxContratos(contratosChile, selectedYear, getDolarValueChile, intermediariosChileArr, pctMapChile),
    [calcComisionApxContratos, contratosChile, selectedYear, getDolarValueChile, intermediariosChileArr, pctMapChile]
  );

  const comisionApxContratosMensualPeru = useMemo(
    () => calcComisionApxContratos(contratosPeru, selectedYear, getDolarValuePeru, intermediariosPeruArr, pctMapPeru),
    [calcComisionApxContratos, contratosPeru, selectedYear, getDolarValuePeru, intermediariosPeruArr, pctMapPeru]
  );

  // ─── DESGLOSE por factura de contratos (solo para el modal "Costo intermediación") ───
  const comisionesAgenciaContratosPorMes = useMemo(
    () =>
      calcDesgloseAgenciaContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais,
        pctMap
      ),
    [
      calcDesgloseAgenciaContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientesArr,
      intermediariosArr,
      pais,
      pctMap,
    ]
  );
  const comisionesBrokerContratosPorMes = useMemo(
    () =>
      calcDesgloseBrokerContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        clientesArr,
        intermediariosArr,
        pais,
        pctMap
      ),
    [
      calcDesgloseBrokerContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientesArr,
      intermediariosArr,
      pais,
      pctMap,
    ]
  );
  const apxContratosPorMes = useMemo(
    () => calcDesgloseApxContratos(contratosData, selectedYear, getDolarValue, intermediariosArr, pctMap),
    [calcDesgloseApxContratos, contratosData, selectedYear, getDolarValue, intermediariosArr, pctMap]
  );

  // ─── COMISIÓN COMERCIAL de contratos ───

  const comisionComercialContratosMensual = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosData,
        selectedYear,
        getDolarValue,
        clientes,
        intermediarios,
        edificios,
        pais,
        snapMap,
        pctMap
      ),
    [
      calcComisionComercialContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientes,
      intermediarios,
      edificios,
      snapMap,
      pctMap,
      pais,
    ]
  );

  const comisionComercialContratosMensualAnoAnterior = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosData,
        selectedYear - 1,
        getDolarValue,
        clientes,
        intermediarios,
        edificios,
        pais,
        snapMap,
        pctMap
      ),
    [
      calcComisionComercialContratos,
      contratosData,
      selectedYear,
      getDolarValue,
      clientes,
      intermediarios,
      edificios,
      snapMap,
      pctMap,
      pais,
    ]
  );

  const comisionComercialContratosMensualChile = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosChile,
        selectedYear,
        getDolarValueChile,
        clientesChile,
        intermediariosChile,
        edificiosChile,
        "Chile",
        snapMapChile,
        pctMapChile
      ),
    [
      calcComisionComercialContratos,
      contratosChile,
      selectedYear,
      getDolarValueChile,
      clientesChile,
      intermediariosChile,
      edificiosChile,
      snapMapChile,
      pctMapChile,
    ]
  );

  const comisionComercialContratosMensualPeru = useMemo(
    () =>
      calcComisionComercialContratos(
        contratosPeru,
        selectedYear,
        getDolarValuePeru,
        clientesPeru,
        intermediariosPeru,
        edificiosPeru,
        "Perú",
        snapMapPeru,
        pctMapPeru
      ),
    [
      calcComisionComercialContratos,
      contratosPeru,
      selectedYear,
      getDolarValuePeru,
      clientesPeru,
      intermediariosPeru,
      edificiosPeru,
      snapMapPeru,
      pctMapPeru,
    ]
  );

  return {
    facturasPorMesContratos,
    comisionesEdificiosPorMesContratos,
    ingresosContratosMensual,
    ingresosContratosMensualAnoAnterior,
    ingresosContratosMensualChile,
    ingresosContratosMensualPeru,
    contratosMensualesCombinados,
    comisionEdificiosContratosMensual,
    comisionEdificiosContratosMensualAnoAnterior,
    comisionEdificiosContratosMensualChile,
    comisionEdificiosContratosMensualPeru,
    comisionAgenciaContratosMensual,
    comisionAgenciaContratosMensualAnoAnterior,
    comisionAgenciaContratosMensualChile,
    comisionAgenciaContratosMensualPeru,
    comisionBrokerContratosMensual,
    comisionBrokerContratosMensualAnoAnterior,
    comisionBrokerContratosMensualChile,
    comisionBrokerContratosMensualPeru,
    comisionesAgenciaContratosPorMes,
    comisionesBrokerContratosPorMes,
    comisionApxContratosMensual,
    comisionApxContratosMensualAnoAnterior,
    comisionApxContratosMensualChile,
    comisionApxContratosMensualPeru,
    apxContratosPorMes,
    comisionComercialContratosMensual,
    comisionComercialContratosMensualAnoAnterior,
    comisionComercialContratosMensualChile,
    comisionComercialContratosMensualPeru,
  };
}
